import React from 'react';
import Button from '@mui/material/Button';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { Link } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import { useAppSelector } from '../../app/hooks';
import { getHelpURL } from '../../services/user';
import { useProfileVersion } from '../Vendors/profileVersion';
import { NavDestination } from './NavigationListItems';
import AccountMenu from './AccountMenu';

interface HeaderBarProps {
  activeItem?: NavDestination;
}

const TPNShield = `${process.env.PUBLIC_URL}/assets/TPN-Logo-Shield.png`;

const Header = styled('header')({
  height: 48,
  flexShrink: 0,
  display: 'flex',
  alignItems: 'stretch',
  background: '#1976D2',
  color: '#fff',
  position: 'relative',
  zIndex: 40,
});

const LogoBlock = styled(Link)({
  width: 90,
  flexShrink: 0,
  background: '#3868B9',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

const ShieldImg = styled('img')({ height: 36 });

const ActiveTitle = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '0 18px',
  '& svg': { fontSize: 22 },
  '& h1': { margin: 0, fontSize: '1rem', fontWeight: 500, color: '#fff' },
});

const Spacer = styled('div')({ flex: 1 });

const RightGroup = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: 14,
  padding: '0 16px',
});

// White text/icon re-asserted across states because this Button renders as an
// <a>, which the app-wide styleOverrides.css would otherwise turn blue on hover.
const SupportButton = styled(Button)<{
  component?: React.ElementType;
  href?: string;
  target?: string;
  rel?: string;
}>({
  height: 30,
  color: '#fff',
  border: '1px solid rgba(255,255,255,0.85)',
  textTransform: 'none',
  fontWeight: 500,
  '&:visited': { color: '#fff' },
  '&:active': { color: '#fff' },
  '&:hover': { color: '#fff', border: '1px solid #fff', background: 'rgba(255,255,255,0.14)' },
});

/** Fixed 48px top header: brand shield, active-destination title, Support, account menu. */
export default function HeaderBar({ activeItem }: HeaderBarProps) {
  const { user } = useAppSelector((state) => state.user);
  const { showV2 } = useProfileVersion();
  const ActiveIcon = activeItem?.icon;

  return (
    <Header>
      {/* v2: logo block matches the header blue instead of the darker #3868B9 */}
      <LogoBlock to="/" aria-label="TPN+ home" sx={showV2 ? { background: '#1976D2' } : undefined}>
        <ShieldImg src={TPNShield} alt="TPN+ home" />
      </LogoBlock>

      {activeItem && (
        <ActiveTitle>
          {ActiveIcon && <ActiveIcon aria-hidden="true" />}
          <h1>{activeItem.label}</h1>
        </ActiveTitle>
      )}

      <Spacer />

      <RightGroup>
        <SupportButton
          component="a"
          href={getHelpURL(user?.type)}
          target="_blank"
          rel="noreferrer"
          startIcon={<HelpOutlineIcon />}
          sx={showV2 ? {
            background: 'rgba(255,255,255,0.14)',
            '&:hover': { color: '#fff', border: '1px solid #fff', background: '#142F53' },
          } : undefined}
        >
          {showV2 ? 'Need Support' : 'Support'}
        </SupportButton>
        <AccountMenu />
      </RightGroup>
    </Header>
  );
}
